import type { CheckoutAttribution } from '@shared/game/types';

export type CtaLocation = 'hero' | 'shop' | 'mobile' | 'social';

const STORAGE_KEY = 'reino-tableros.attribution.v1';
const VALUE_PATTERN = /^[a-zA-Z0-9._:-]{1,64}$/;
const SOCIAL_SOURCES = new Set(['tiktok', 'instagram', 'facebook', 'youtube']);

interface StoredAttribution {
  source?: string;
  medium?: string;
}

function readStored(): StoredAttribution {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as StoredAttribution;
    return {
      source: typeof parsed.source === 'string' && VALUE_PATTERN.test(parsed.source) ? parsed.source : undefined,
      medium: typeof parsed.medium === 'string' && VALUE_PATTERN.test(parsed.medium) ? parsed.medium : undefined,
    };
  } catch {
    return {};
  }
}

function writeStored(value: StoredAttribution): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Sin sessionStorage la atribución sigue funcionando en memoria durante esta página.
  }
}

export function captureAttribution(): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const source = params.get('utm_source')?.trim();
  const medium = params.get('utm_medium')?.trim();
  const current = readStored();
  const next: StoredAttribution = {
    source: source && VALUE_PATTERN.test(source) ? source.slice(0, 64) : current.source,
    medium: medium && VALUE_PATTERN.test(medium) ? medium.slice(0, 64) : current.medium,
  };
  if (next.source || next.medium) writeStored(next);
}

function isSocialSource(source: string | undefined): boolean {
  return Boolean(source && SOCIAL_SOURCES.has(source.toLowerCase()));
}

export function getAttribution(location: CtaLocation): CheckoutAttribution {
  const stored = readStored();
  const social = isSocialSource(stored.source);
  return {
    utm_source: stored.source ?? 'reino-tableros',
    utm_medium: stored.medium ?? (social ? 'social' : 'owned'),
    utm_campaign: 'reino-tableros-pack',
    utm_content: social ? 'social' : location,
  };
}

export function applyAttributionToUrl(location: CtaLocation): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  const attribution = getAttribution(location);
  for (const [key, value] of Object.entries(attribution)) url.searchParams.set(key, value);
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}
